<template>
  <pui-flex class="file-search-form file-search-form__root" flex="1">
    <pui-flex
      v-if="hasFormError"
      direction="column"
      alignItems="center"
      class="file-search-form__content file-search-form__error"
    >
      <i class="error-icon mdi mdi-close-circle-outline" />
      <div class="error-primary">
        {{ $t('dam.files.search.filters.error.primary') }}
      </div>
      <div>
        <i18n class="error-secondary" path="dam.files.search.filters.error.secondary.main">
          <template #click>
            <pui-button @click.stop="retrieveForm" class="error-secondary-button">
              {{ $t('dam.files.search.filters.error.secondary.click') }}
            </pui-button>
          </template>
        </i18n>
      </div>
    </pui-flex>
    <pui-flex v-else :class="mainClass">
      <pui-common-spinner v-if="loading" position="fixed" hideMessage />

      <pui-tabbed-menu
        v-if="formDefinition && formDefinition.tabs"
        :tabs="formDefinition.tabs"
        :getTabId="getTabId"
        :minInlineTabCount="3"
        :maxInlineTabCount="9"
        class="file-search-form__tabs"
        classBody="file-search-form__tabs-body"
        classTab="file-search-form__tab"
      >
        <template #headerTitle="{ tab }">
          <div
            v-if="tab._countSelectedAttribute && tab._countSelectedAttribute > 0"
            class="file-search-form__count-opposite-spacing file-search-form__end-spacer"
          ></div>

          <div class="file-search-form__tab-header-title">
            <template v-if="tab._labelKey">
              {{ $t(tab._labelKey) }}
            </template>
            <template v-else>
              {{ piivoTranslate(tab) }}
            </template>
          </div>

          <div
            v-if="tab._countSelectedAttribute && tab._countSelectedAttribute > 0"
            class="file-search-form__end-spacer"
          >
            <div class="file-search-form__count">
              {{ tab._countSelectedAttribute }}
            </div>
          </div>
        </template>

        <template v-if="hasTabs" #default="{ tab }">
          <pui-search-attribute-filter
            v-for="attributeFilter in tab._attributeFilters"
            :key="attributeFilter.itemId"
            :attributeFilter="attributeFilter"
            :value="getFormValue(attributeFilter.itemId)"
            :loadOptionsFunction="getPossibleValues"
            @change="attributeFilterChanged(attributeFilter, $event)"
            :lazyOptions="!attributeFilter._frontendFilter"
            class="file-search-form__attribute-panel"
          ></pui-search-attribute-filter>
        </template>

        <template #bodyBefore="bodyBeforeProps">
          <slot v-bind="bodyBeforeProps" name="bodyBefore"></slot>
        </template>

        <template #bodyAfter="bodyAfterProps">
          <slot v-bind="bodyAfterProps" name="bodyAfter"></slot>
        </template>
      </pui-tabbed-menu>
    </pui-flex>
  </pui-flex>
</template>

<script>
import Vue from 'vue';

import { AttributeTypes } from '../../../../common/constants';
import { getAttributeLinkOptions } from '../../../helpers/formAttributes';
import puiSearchAttributeFilter from './SearchAttributeFilter';

export default {
  components: {
    puiSearchAttributeFilter,
  },
  props: {
    /**
     * Retrieves the form definition to display
     */
    retrieveFormDefinition: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // FORM
      formDefinition: null,
      attributesFormValues: [],
      // OTHER
      loading: false,
      hasFormError: true,
      // COUNTED ATTRIBUTES,
      countedAttributeFilters: [],
    };
  },
  computed: {
    /**
     * @returns {boolean} if there are form tabs
     */
    hasTabs() {
      return (
        this.formDefinition &&
        Array.isArray(this.formDefinition.tabs) &&
        this.formDefinition.tabs.length
      );
    },
    /**
     * @returns {object} class object for the main container
     */
    mainClass() {
      return {
        'file-search-form__content file-search-form__main': true,
        loading: this.loading,
      };
    },
  },
  methods: {
    // FILTER
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    // FORM
    getTabId(tab, tabIndex) {
      return tab.itemId;
    },
    /**
     * Retrieves the form for the selected folders
     *
     * @returns {Promise<void>}
     */
    async retrieveForm() {
      this.loading = true;
      this.hasFormError = false;

      try {
        this.formDefinition = await this.retrieveFormDefinition();
      } catch (err) {
        if (!Vue.http.isCancel(err)) {
          this.hasFormError = true;
        }
      }

      this.loading = false;
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - parameters for the load
     * @param {object} parameters.context - additional context to load the values
     * @returns {Promise<object[]>} options for the attribute
     */
    async getPossibleValues(attribute, { context }) {
      if (attribute.type === AttributeTypes.LINKS) {
        return getAttributeLinkOptions(attribute);
      }

      return Promise.resolve([]);
    },
    /**
     * @param {string} itemId - the id of the attibute whose
     * value to retrieve
     * @returns {*} the value for the AttributePanel
     */
    getFormValue(itemId) {
      for (let i = 0; i < this.attributesFormValues.length; i++) {
        const currentAttribute = this.attributesFormValues[i];
        if (currentAttribute.attributeId === itemId) {
          return currentAttribute.value;
        }
      }
    },
    /**
     * Updates the value stored in 'formValues' for the rendered attribute definition
     *
     * @param {object} attributeFilter - the attribute object that was rendered
     * @param {*} newValue - the new value emitted by AttributePanel
     * @returns {void}
     */
    attributeFilterChanged(attributeFilter, newValue) {
      // Clearing the selected value(s) in a link attribute, will trigger this method,
      // but it means we have to decrement the count, since the attribute has no more value
      const incrementCount = attributeFilter.type !== AttributeTypes.LINKS || newValue.length > 0;

      this.updateAttributeCountForTabs(attributeFilter, incrementCount);
      let didFindAttr = false;

      // Update the value in 'formValues' if exists
      for (let i = 0; i < this.attributesFormValues.length; i++) {
        const currentAttributeValue = this.attributesFormValues[i];
        if (currentAttributeValue.attributeId === attributeFilter.itemId) {
          // Store whole object to pass to AtrtributePanel
          currentAttributeValue.value = newValue;
          didFindAttr = true;

          this.$emit('updateAttributeValue', attributeFilter, currentAttributeValue.value);
        }
      }

      // If we did not have a value for this attribute
      // push it to the array
      if (!didFindAttr) {
        const newAttributeValue = {
          name: attributeFilter.attribute.fieldName,
          attributeAlias: attributeFilter.alias,
          attributeId: attributeFilter.itemId,
          value: newValue,
          type: attributeFilter.attribute.type,
        };
        this.attributesFormValues.push(newAttributeValue);
        this.$emit('addAttributeValue', attributeFilter, newAttributeValue.value);
      }
    },
    /**
     * Updates the attribute count for all tabs, by adding or subtracting
     * to the current tab's count
     *
     * @param {Object} attributeFilter - the attribute to count
     * @param {String} attribute.itemId - the id of the attribute
     * @param {Boolean} [incrementCount=true] - whether to add or subtract from the count
     * @returns {void}
     */
    updateAttributeCountForTabs(attributeFilter, incrementCount = true) {
      const existingAttributeIndex = this.countedAttributeFilters.findIndex(
        attr => attributeFilter.itemId === attr.itemId
      );

      if (existingAttributeIndex !== -1 && incrementCount) {
        // When adding the attribute to the counted attrs, if it is already counted, ignore it
        return;
      }

      if (incrementCount) {
        // Save the attribute so next time we don't count it again
        this.countedAttributeFilters.push(attributeFilter);
      }

      // If no form definition, can't do anything
      if (!this.formDefinition || !this.formDefinition.tabs) {
        return;
      }

      for (const currentTab of this.formDefinition.tabs) {
        // If this tab doesn't have attributes, or the attribute is not in
        // this tab, skip this iteration
        if (
          !currentTab._attributeFilters ||
          !currentTab._attributeFilters.find(
            attrFilter => attributeFilter.itemId === attrFilter.itemId
          )
        ) {
          continue;
        }

        const currentTabCount = currentTab._countSelectedAttribute || 0;

        // Update the count for the tab
        if (incrementCount) {
          this.$set(currentTab, '_countSelectedAttribute', currentTabCount + 1);
        } else {
          this.$set(currentTab, '_countSelectedAttribute', currentTabCount - 1);

          // Remove the attr from our count list, so we can count it next time
          // the user sets a value for it
          if (existingAttributeIndex !== -1) {
            this.countedAttributeFilters.splice(existingAttributeIndex, 1);
          }
        }
      }
    },
    // EXPOSED METHODS
    /**
     * Removes an attribute form value
     *
     * @param {string} attributeId - the itemId of the attribute whose value to remove
     * @returns {void}
     */
    removeAttributeValue(attributeId) {
      this.updateAttributeCountForTabs({ itemId: attributeId }, false);
      const index = this.attributesFormValues.findIndex(
        objValue => objValue.attributeId === attributeId
      );
      if (index !== -1) {
        this.attributesFormValues.splice(index, 1);
      }
    },
  },
  mounted() {
    this.retrieveForm();
  },
};
</script>
